import supabase from '../supabase/supabase';

const DEFAULT_PREFERENCES = {
  new_nearby: true,
  expiring_soon: true,
  provider_updates: true,
  radius_km: 10,
  categories: [],
  browser_notifications: false,
  email_search_matches: true,
  email_messages: true,
  email_requests: true,
  email_reservations: true,
};

const isMissingFeatureError = (error) => ['42P01', 'PGRST205', 'PGRST204'].includes(error?.code)
  || String(error?.message || '').includes('Could not find the table');

const unwrap = (result, fallback) => {
  if (!result.error) return result.data ?? fallback;
  if (isMissingFeatureError(result.error)) return fallback;
  throw result.error;
};

export const communityService = {
  defaultPreferences: DEFAULT_PREFERENCES,

  async getDashboard(userId) {
    if (!userId) return {
      favorites: [], subscriptions: [], searchSubscriptions: [], notifications: [], preferences: DEFAULT_PREFERENCES, blocks: [],
    };

    const [favorites, subscriptions, searchSubscriptions, notifications, preferences, blocks] = await Promise.all([
      supabase.from('favorites').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
      supabase.from('provider_subscriptions').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
      supabase.from('search_subscriptions').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
      supabase.from('notifications').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(100),
      supabase.from('notification_preferences').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('user_blocks').select('*').eq('blocker_id', userId),
    ]);

    let preferenceData = unwrap(preferences, null);
    if (!preferenceData) {
      const defaults = { user_id: userId, ...DEFAULT_PREFERENCES };
      const initialized = await supabase.from('notification_preferences')
        .upsert(defaults, { onConflict: 'user_id' })
        .select()
        .single();
      preferenceData = unwrap(initialized, defaults);
    }

    return {
      favorites: unwrap(favorites, []),
      subscriptions: unwrap(subscriptions, []),
      searchSubscriptions: unwrap(searchSubscriptions, []),
      notifications: unwrap(notifications, []),
      preferences: { ...DEFAULT_PREFERENCES, ...(preferenceData || {}) },
      blocks: unwrap(blocks, []),
    };
  },

  async setFavorite(userId, targetType, targetId, enabled) {
    if (enabled) {
      const result = await supabase.from('favorites').upsert({
        user_id: userId,
        target_type: targetType,
        target_id: String(targetId),
      }, { onConflict: 'user_id,target_type,target_id' }).select().single();
      return unwrap(result, null);
    }

    const result = await supabase.from('favorites').delete()
      .eq('user_id', userId)
      .eq('target_type', targetType)
      .eq('target_id', String(targetId));
    unwrap(result, null);
    return null;
  },

  async setProviderSubscription(userId, providerId, enabled) {
    if (enabled) {
      await this.savePreferences(userId, DEFAULT_PREFERENCES);
      const result = await supabase.from('provider_subscriptions').upsert({
        user_id: userId,
        provider_id: String(providerId),
      }, { onConflict: 'user_id,provider_id' }).select().single();
      return unwrap(result, null);
    }

    const result = await supabase.from('provider_subscriptions').delete()
      .eq('user_id', userId)
      .eq('provider_id', String(providerId));
    unwrap(result, null);
    return null;
  },

  async savePreferences(userId, preferences) {
    const payload = {
      user_id: userId,
      new_nearby: Boolean(preferences.new_nearby),
      expiring_soon: Boolean(preferences.expiring_soon),
      provider_updates: Boolean(preferences.provider_updates),
      radius_km: Number(preferences.radius_km) || 10,
      categories: Array.isArray(preferences.categories) ? preferences.categories : [],
      browser_notifications: Boolean(preferences.browser_notifications),
      email_search_matches: preferences.email_search_matches !== false,
      email_messages: preferences.email_messages !== false,
      email_requests: preferences.email_requests !== false,
      email_reservations: preferences.email_reservations !== false,
      updated_at: new Date().toISOString(),
    };
    const result = await supabase.from('notification_preferences').upsert(payload).select().single();
    return unwrap(result, payload);
  },

  async markNotificationRead(userId, notificationId) {
    const result = await supabase.from('notifications').update({ read_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', userId)
      .select()
      .single();
    return unwrap(result, null);
  },

  async markAllNotificationsRead(userId) {
    const result = await supabase.from('notifications').update({ read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('read_at', null);
    unwrap(result, null);
  },

  async createReport(userId, targetType, targetId, reason, details = '') {
    if (!userId) throw new Error('Bitte melde dich an, um Inhalte zu melden.');
    if (!targetId) throw new Error('Der zu meldende Inhalt konnte nicht bestimmt werden.');

    const { data, error } = await supabase.rpc('submit_report', {
      p_target_type: targetType,
      p_target_id: String(targetId),
      p_reason: reason,
      p_details: details.trim() || null,
    });
    if (error) throw error;
    return data;
  },

  async setBlocked(userId, blockedId, enabled) {
    if (enabled) {
      const result = await supabase.from('user_blocks').upsert({
        blocker_id: userId,
        blocked_id: blockedId,
      }, { onConflict: 'blocker_id,blocked_id' }).select().single();
      return unwrap(result, null);
    }

    const result = await supabase.from('user_blocks').delete()
      .eq('blocker_id', userId)
      .eq('blocked_id', blockedId);
    unwrap(result, null);
    return null;
  },

  async createSearchSubscription(userId, subscription) {
    const payload = {
      ...subscription,
      user_id: userId,
      name: String(subscription.name || '').trim(),
      query: String(subscription.query || '').trim(),
      categories: Array.isArray(subscription.categories) ? subscription.categories : [],
      allergens_excluded: Array.isArray(subscription.allergens_excluded) ? subscription.allergens_excluded : [],
      times_of_day: Array.isArray(subscription.times_of_day) ? subscription.times_of_day : [],
      expiry_options: Array.isArray(subscription.expiry_options) ? subscription.expiry_options : [],
      updated_at: new Date().toISOString(),
    };
    const result = await supabase.from('search_subscriptions').insert(payload).select().single();
    return unwrap(result, payload);
  },

  async updateSearchSubscription(userId, subscriptionId, updates) {
    const result = await supabase.from('search_subscriptions').update({
      ...updates,
      updated_at: new Date().toISOString(),
    }).eq('id', subscriptionId).eq('user_id', userId).select().single();
    return unwrap(result, null);
  },

  async deleteSearchSubscription(userId, subscriptionId) {
    const result = await supabase.from('search_subscriptions').delete().eq('id', subscriptionId).eq('user_id', userId);
    unwrap(result, null);
  },

  async getTrust(userId) {
    if (!userId) return null;
    const result = await supabase.from('profile_trust').select('*').eq('user_id', userId).maybeSingle();
    return unwrap(result, null);
  },

  async getModerationQueue() {
    const result = await supabase.from('reports').select('*').order('created_at', { ascending: false }).limit(200);
    return unwrap(result, []);
  },

  async updateReport(reportId, status, moderatorId, moderatorNote = '') {
    const result = await supabase.from('reports').update({
      status,
      moderator_id: moderatorId,
      moderator_note: moderatorNote.trim() || null,
      resolved_at: ['resolved', 'dismissed'].includes(status) ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    }).eq('id', reportId).select().single();
    return unwrap(result, null);
  },
};
